import { NavLink, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { subject } from "../lib/data";

type Props = { children: ReactNode; onOpenCmdk: () => void };

const navItems = [
  { to: "/",             label: "Home",          badge: "",    alert: false, icon: IconHome },
  { to: "/ask",          label: "Ask",           badge: "",    alert: false, icon: IconChat },
  { to: "/investigate",  label: "Investigations",badge: "2",   alert: false, icon: IconMagnify },
  { to: "/signal",       label: "Signals",       badge: "1",   alert: true,  icon: IconBell },
  { to: "/connections",  label: "Connections",   badge: "ai",  alert: false, icon: IconBrain },
  { to: "/drivers",      label: "Drivers",       badge: "new", alert: false, icon: IconCompass },
  { to: "/crompton",     label: "Client lens",   badge: "new", alert: false, icon: IconTarget },
  { to: "/dashboards",   label: "Brand Health",  badge: "",    alert: false, icon: IconChart },
  { to: "/quotes",       label: "Quote Explorer",badge: "123", alert: false, icon: IconQuote },
  { to: "/repository",   label: "Data",          badge: "",    alert: false, icon: IconLayers },
];

export default function Chrome({ children, onOpenCmdk }: Props) {
  const nav = useNavigate();
  return (
    <div className="shell">
      <header className="top">
        <div className="brand-cell" onClick={() => nav("/")}>
          <div className="brand-mark" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12 H6 L8.5 6 L12 18 L15.5 9 L17.5 13 H22" stroke="white" strokeWidth="2.2" />
            </svg>
          </div>
          <div className="brand-name">
            <span className="brand-name-primary">InfoLeap</span>
            <span className="brand-name-sub">Pulse</span>
          </div>
        </div>

        <div className="top-center">
          <span className="crumb">
            Info Leap <span className="soft">· Demo</span>
            <span className="chev">▾</span>
          </span>
          <span className="crumb-sep">/</span>
          <span className="crumb">
            {subject.name} <span className="muted-soft">+ Fans</span>
            <span className="chev">▾</span>
          </span>
        </div>

        <div className="top-actions">
          <button className="cmdk-trigger" onClick={onOpenCmdk} aria-label="Open command palette">
            Jump to…
            <kbd>⌘K</kbd>
          </button>
          <button className="iconbtn" aria-label="Notifications" title="Notifications">
            <IconBell />
            <span className="pip" />
          </button>
          <button className="iconbtn" aria-label="Help" title="Help">
            <IconHelp />
          </button>
          <div className="avatar" title="CMO Preview">C</div>
        </div>
      </header>

      <nav className="sidenav">
        <h5>Workspace</h5>
        {navItems.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            className={({ isActive }) => "item" + (isActive ? " active" : "")}
          >
            <it.icon />
            <span>{it.label}</span>
            {it.badge && <span className={"badge" + (it.alert ? " alert" : "")}>{it.badge}</span>}
          </NavLink>
        ))}

        <h5>Active studies</h5>
        <div className="brand-card">
          <div className="hd">Cross-category portfolio</div>
          <div className="nm"><span className="dot" /> Mixer Grinder + Fans</div>
          <div className="stats">
            <div className="st"><div className="k">MG n</div><div className="v">{subject.respondents.toLocaleString()}</div></div>
            <div className="st"><div className="k">Fans n</div><div className="v">1,464</div></div>
            <div className="st"><div className="k">Categories</div><div className="v">2</div></div>
            <div className="st"><div className="k">Waves</div><div className="v">{subject.waves}</div></div>
          </div>
        </div>

        <h5>System</h5>
        <NavLink to="/settings" className={({ isActive }) => "item" + (isActive ? " active" : "")}>
          <IconGear />
          <span>Settings</span>
        </NavLink>

        <div className="spacer" />

        <div className="cloud">
          <span className="dot-live" />
          <span>Azure EU-W1 · healthy</span>
        </div>
      </nav>

      <main className="main">{children}</main>
    </div>
  );
}

function IconHome() {
  return (<svg viewBox="0 0 24 24"><path d="M3 12 12 3l9 9"/><path d="M5 10v10h14V10"/></svg>);
}
function IconLayers() {
  return (<svg viewBox="0 0 24 24"><polygon points="12 2 22 8 12 14 2 8 12 2"/><polyline points="2 14 12 20 22 14"/></svg>);
}
function IconChart() {
  return (<svg viewBox="0 0 24 24"><line x1="4" y1="20" x2="20" y2="20"/><line x1="6" y1="16" x2="6" y2="20"/><line x1="11" y1="10" x2="11" y2="20"/><line x1="16" y1="13" x2="16" y2="20"/></svg>);
}
function IconChat() {
  return (<svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2Z"/></svg>);
}
function IconMagnify() {
  return (<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><line x1="16" y1="16" x2="21" y2="21"/></svg>);
}
function IconBell() {
  return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>);
}
function IconHelp() {
  return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12" y2="17"/></svg>);
}
function IconQuote() {
  return (<svg viewBox="0 0 24 24"><path d="M7 7h4v4H7a3 3 0 0 0-3 3v2" /><path d="M15 7h4v4h-4a3 3 0 0 0-3 3v2" /></svg>);
}
function IconBrain() {
  return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18a4 4 0 0 1-4-4V8a3 3 0 0 1 6 0"/><path d="M15 18a4 4 0 0 0 4-4V8a3 3 0 0 0-6 0"/><path d="M9 18h6"/><path d="M9 13h6"/></svg>);
}
function IconCompass() {
  return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>);
}
function IconTarget() {
  return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>);
}
function IconGear() {
  return (<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3.9a7 7 0 0 0-2.1-1.2L14 3h-4l-.5 2.6a7 7 0 0 0-2.1 1.2l-2.3-.9-2 3.4 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.4 2.3-.9a7 7 0 0 0 2.1 1.2L10 21h4l.5-2.6a7 7 0 0 0 2.1-1.2l2.3.9 2-3.4-2-1.5c.1-.4.1-.8.1-1.2z"/></svg>);
}
